// TODO: change Popover to Dialog
import {
  Button,
  ButtonProps,
  Checkbox,
  CheckboxGroup,
  ClearButtonBehaviour,
  DateInput,
  NumericInput,
  TextInput,
} from '@infotech-soe/erp-ui-kit';
import Popover from '@infotech-soe/erp-ui-kit/dist/components/Popover';
import { TDatasetQueryParams } from '@infotech-soe/merps/types/Queries';
import { tg } from '@infotech-soe/metaschema';
import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { LOCALES } from '../../../../constants/intl';
import { DatasetConfig } from '../../../../models/user-config/DisplayModeConfig';
import {
  DatasetColumnFormat,
  DatasetGridColumn,
} from '../../../../services/dataset/view-model/DatasetGridColumn';
import { DatasetGridRow } from '../../../../services/dataset/view-model/DatasetGridRow';
import UniqueId from '../../../../services/uid/UniqueId';
import { useAppDispatch } from '../../../../store/hooks';
import categoryActions from '../../../../store/slices/category';
import dataset from '../../../../store/slices/dataset';
import { DATASET_ID_PREFIX } from '../constants';

interface FilterPopoverProps {
  open: boolean;
  config: DatasetConfig;
  datasetId: UniqueId;
  activeCell: number;
  activeRow: number;
  locale: LOCALES;
  orderedColumns: DatasetGridColumn[];
  rows: DatasetGridRow[];
  datasetName: string;
  categoryId: UniqueId;
  conditions: TDatasetQueryParams;
  closePopover: () => void;
}

const messages = defineMessages({
  confirmButton: {
    id: 'core.form.ok',
    defaultMessage: 'Ok',
  },
  cancelButton: {
    id: 'ui.button.cancel',
    defaultMessage: 'Сancel',
  },
  clearButton: {
    id: 'ui.clear',
    defaultMessage: 'Clear',
  },
  from: {
    id: 'core.category.grid.ag.of',
    defaultMessage: 'from',
  },
  to: {
    id: 'core.category.grid.ag.to',
    defaultMessage: 'to',
  },
  notSet: {
    id: 'core.category.grid.ag.notSet',
    defaultMessage: 'Not set',
  },
});

const FilterPopover: React.FC<FilterPopoverProps> = ({
  open,
  config,
  datasetId,
  categoryId,
  activeCell,
  activeRow,
  locale,
  orderedColumns,
  rows,
  datasetName,
  conditions,
  closePopover,
}) => {
  const datasetSelector = `#${DATASET_ID_PREFIX}${datasetId}`;
  const cellRefSelector = `${datasetSelector} [data-row="${activeRow}"][data-cell="${activeCell}"]`;
  const headerCellSelector = `${datasetSelector} [data-columnindex="${activeCell}"]`;

  const { formatMessage } = useIntl();
  const { rules } = config.preset[config.currentPreset];
  const {
    domain: {
      format: domainFormat,
      type,
      subtype,
      class: domainClass,
      precision,
      values = [],
      align,
    },
    field,
    title,
    formatter,
    format,
  } = orderedColumns[activeCell];
  const withoutThousandSeparator = subtype && ['int', 'year'].includes(subtype);
  const renamedTitle = rules[field].title?.[locale] || title;
  const currentFieldsConditions = conditions.fieldsConditions;
  const cellValue = currentFieldsConditions[field]
    ? currentFieldsConditions[field][0]
    : rows[activeRow]
      ? rows[activeRow][field]
      : '';

  const options = useMemo(
    () =>
      formatter
        ? values.map(currentValue => ({
            value: currentValue.toString(),
            label: formatter(currentValue, { row: rows[activeRow] }),
          }))
        : [],
    [formatter, values, rows, activeRow]
  );

  const selected =
    currentFieldsConditions[field] ||
    values
      .map(currentValue =>
        currentValue === cellValue ? currentValue.toString() : undefined
      )
      .filter(tg.isSomething);

  const [selectedValues, setSelectedValues] = useState<Array<
    string | number | null
  > | null>(selected);

  const [textValue, setTextValue] = useState(
    align ? cellValue?.toString().trimStart() : cellValue
  );

  const [rangeFrom, setRangeFrom] = useState<string | undefined>(
    currentFieldsConditions[field]
      ? currentFieldsConditions[field][0]
      : cellValue?.toString()
  );
  const [rangeTo, setRangeTo] = useState<string | undefined>(
    currentFieldsConditions[field]
      ? currentFieldsConditions[field][1]
      : cellValue?.toString()
  );
  const [notSet, setNotSet] = useState<boolean>(
    currentFieldsConditions[field]
      ? currentFieldsConditions[field][2]
      : !cellValue
  );
  const [valid, setValid] = useState(true);

  const dispatch = useAppDispatch();

  const [containerRef, setContainerRef] = useState<HTMLDivElement | null>(null);

  const updateableRef = useCallback((node: HTMLDivElement | null) => {
    if (node !== null) {
      setContainerRef(node);
    }
  }, []);

  const validateRange = useCallback(
    (range: Array<boolean | string | number | null | undefined>) => {
      const [from, to] = range;
      const validFlag =
        from === undefined ||
        to === undefined ||
        (from !== null && to !== null && from <= to);
      setValid(validFlag);
      return validFlag;
    },
    [setValid]
  );

  const onSubmit = useCallback(() => {
    const fieldsConditions: Record<
      string,
      Array<boolean | string | number | null | undefined>
    > = {
      ...conditions.fieldsConditions,
      [field]: [],
    };
    switch (type) {
      case 'string': {
        fieldsConditions[field] = [textValue];
        break;
      }
      case 'number':
        if (rangeFrom || rangeTo) {
          fieldsConditions[field] = [rangeFrom, rangeTo];
        }
        break;
      case 'object': {
        if (domainClass === 'Date' && (rangeFrom || rangeTo || notSet)) {
          fieldsConditions[field] = [rangeFrom, rangeTo, notSet];
          if (!validateRange(fieldsConditions[field])) {
            return;
          }
        }
        break;
      }
      case 'enum': {
        fieldsConditions[field] = selectedValues ?? [];
        break;
      }
    }

    if (!fieldsConditions[field].some(Boolean)) {
      delete fieldsConditions[field];
    }

    closePopover();
    dispatch(
      categoryActions.updateFieldsConditions({
        categoryId,
        datasetName,
        fieldsConditions,
      })
    );
    dispatch(dataset.update({ datasetId, forceDOMFocus: true }));
  }, [
    conditions.fieldsConditions,
    field,
    type,
    closePopover,
    dispatch,
    categoryId,
    datasetName,
    datasetId,
    rangeFrom,
    rangeTo,
    notSet,
    textValue,
    validateRange,
    domainClass,
    selectedValues,
  ]);

  const onChangeDateInput = useCallback(
    (side: 'from' | 'to') =>
      (date: Date | null): void => {
        if (side === 'from') {
          setRangeFrom(date?.toISOString());
          validateRange([date?.toISOString(), rangeTo]);
        } else {
          setRangeTo(date?.toISOString());
          validateRange([rangeFrom, date?.toISOString()]);
        }
      },
    [rangeTo, rangeFrom, validateRange]
  );

  const onChangeNotSet = useCallback(() => {
    setRangeFrom(undefined);
    setRangeTo(undefined);
    setNotSet(!notSet);
  }, [notSet]);

  const onChangeNumericInput = useCallback(
    (side: 'from' | 'to') =>
      (value: number | null): void => {
        if (side === 'from') {
          setRangeFrom(value?.toString());
        } else {
          setRangeTo(value?.toString());
        }
      },
    []
  );

  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.select();
      } else {
        containerRef?.getElementsByTagName('input')[0].focus();
      }
    }, 0);
  }, [containerRef]);

  const renderInput = useMemo(
    () => (
      <TextInput
        ref={inputRef}
        value={textValue}
        onChange={setTextValue}
        onSubmit={onSubmit}
      />
    ),
    [textValue, onSubmit]
  );

  const renderNumericInput = useMemo(
    () => (
      <div className="grid__popover-range">
        <label htmlFor="rangeFrom">{formatMessage(messages.from)}</label>
        <NumericInput
          id="rangeFrom"
          ref={inputRef}
          max={rangeTo ? +rangeTo : undefined}
          value={rangeFrom ? +rangeFrom : null}
          onChange={onChangeNumericInput('from')}
          withoutThousandSeparator={withoutThousandSeparator}
          precision={precision}
          onSubmit={onSubmit}
          onValidate={e => setValid(!e)}
          withClearButton={ClearButtonBehaviour.ALWAYS}
        />
        <label htmlFor="rangeTo">{formatMessage(messages.to)}</label>
        <NumericInput
          id="rangeTo"
          min={rangeFrom ? +rangeFrom : undefined}
          value={rangeTo ? +rangeTo : null}
          onChange={onChangeNumericInput('to')}
          withoutThousandSeparator={withoutThousandSeparator}
          precision={precision}
          onSubmit={onSubmit}
          onValidate={e => setValid(!e)}
          withClearButton={ClearButtonBehaviour.ALWAYS}
        />
      </div>
    ),
    [
      formatMessage,
      rangeFrom,
      onChangeNumericInput,
      withoutThousandSeparator,
      precision,
      onSubmit,
      rangeTo,
    ]
  );

  const renderDateInput = useMemo(
    () => (
      <div className="grid__popover-range">
        <label htmlFor="rangeFrom">{formatMessage(messages.from)}</label>
        <DateInput
          id="rangeFrom"
          format={domainFormat}
          ref={inputRef}
          truncTo={format === DatasetColumnFormat.DATE_TIME_SEC ? 'sec' : 'day'}
          disabled={notSet}
          value={rangeFrom && new Date(rangeFrom)}
          withClearButton={ClearButtonBehaviour.NONE}
          onChange={onChangeDateInput('from')}
          container={containerRef}
          onSubmit={onSubmit}
          invalid={!valid}
        />
        <label htmlFor="rangeTo">{formatMessage(messages.to)}</label>
        <DateInput
          id="rangeTo"
          format={domainFormat}
          truncTo={format === DatasetColumnFormat.DATE_TIME_SEC ? 'sec' : 'day'}
          disabled={notSet}
          value={rangeTo && new Date(rangeTo)}
          withClearButton={ClearButtonBehaviour.NONE}
          onChange={onChangeDateInput('to')}
          container={containerRef}
          onSubmit={onSubmit}
          invalid={!valid}
        />
        <label htmlFor="notSet">{formatMessage(messages.notSet)}</label>
        <Checkbox
          name="notSet"
          value={notSet}
          onChange={onChangeNotSet}
          onSubmit={onSubmit}
        />
      </div>
    ),
    [
      formatMessage,
      domainFormat,
      format,
      notSet,
      rangeFrom,
      onChangeDateInput,
      containerRef,
      onSubmit,
      valid,
      rangeTo,
      onChangeNotSet,
    ]
  );

  const handleClearFields = useCallback(() => {
    setTextValue('');
    setSelectedValues([]);
    setRangeFrom(undefined);
    setRangeTo(undefined);
    setNotSet(false);
    validateRange([]);
  }, [validateRange]);

  const renderCheckboxGroup = useMemo(
    () => (
      <CheckboxGroup
        buttons={['check', 'indeterminate', 'uncheck']}
        optionsClassName="grid__popover-checkbox-group"
        columns={Math.min(
          Math.max(Math.floor(Math.sqrt(options.length)) - 1, 2),
          3
        )}
        options={options}
        value={selectedValues}
        onChange={setSelectedValues}
        onSubmit={onSubmit}
      />
    ),
    [options, selectedValues, onSubmit]
  );

  const renderFields = useMemo(() => {
    switch (type) {
      case 'string':
        return renderInput;
      case 'number':
        return renderNumericInput;
      case 'object':
        if (domainClass === 'Date') {
          return renderDateInput;
        }
        break;
      case 'enum':
        return renderCheckboxGroup;
    }
  }, [
    type,
    domainClass,
    renderInput,
    renderNumericInput,
    renderDateInput,
    renderCheckboxGroup,
  ]);

  const renderButtons = useMemo(() => {
    const buttonsProps = [
      ...(type === 'enum'
        ? []
        : [
            {
              name: 'clear',
              onClick: handleClearFields,
              msg: messages.clearButton,
            },
          ]),
      {
        name: 'submit',
        onClick: onSubmit,
        msg: messages.confirmButton,
        disabled: !valid,
        type: 'submit',
      },
      {
        type: 'cancel',
        name: 'cancel',
        onClick: closePopover,
        msg: messages.cancelButton,
      },
    ];
    return buttonsProps.map(
      ({ onClick, msg, disabled, name, type: btnType = 'button' }) => (
        <Button
          key={name}
          disabled={disabled}
          className="grid__popover-button"
          label={formatMessage(msg)}
          type={btnType as ButtonProps['type']}
          onClick={onClick}
        />
      )
    );
  }, [handleClearFields, closePopover, formatMessage, onSubmit, type, valid]);

  return (
    <Popover
      open={open}
      setOpen={closePopover}
      referenceElement={
        document.querySelector(cellRefSelector) ||
        document.querySelector(headerCellSelector)
      }
      popoverPlacement="bottom"
      popoverRole="dialog"
      nonModal={false}
      enableFlip
      enableShift={1}
      contents={
        <div className="grid__popover" ref={updateableRef}>
          <div className="grid__popover-title">{renamedTitle}</div>
          <div className="grid__popover-content">{renderFields}</div>
          <div className="grid__popover-buttons">{renderButtons}</div>
        </div>
      }
    />
  );
};

export default memo(FilterPopover);
